import { MinusOutlined, PlusOutlined, RedoOutlined, UndoOutlined } from '@ant-design/icons';
import { Button, Modal, Slider } from 'antd';
import { useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { getCroppedImg, type ImageArea } from './cropImage';

const INITIAL_CROPPED_AREA: ImageArea = {
    height: 0,
    width: 0,
    x: 0,
    y: 0,
};

export function ImageCrop({
    open,
    src,
    fileType,
    fileName,
    onChange,
    onClose,
}: {
    open: boolean;
    src: string;
    fileType: string;
    fileName: string;
    onChange: (file: string) => void;
    onClose: () => void;
}) {
    const [crop, setCrop] = useState({ x: 0, y: 0 });

    const [rotation, setRotation] = useState(0);
    const [zoom, setZoom] = useState(1);

    const croppedAreaPixelsRef = useRef<ImageArea>(INITIAL_CROPPED_AREA);

    const resetStates = (): void => {
        setCrop({ x: 0, y: 0 });
        setRotation(0);
        setZoom(1);
        croppedAreaPixelsRef.current = INITIAL_CROPPED_AREA;
    };

    const onCropComplete = (_: ImageArea, croppedAreaPixels: ImageArea): void => {
        croppedAreaPixelsRef.current = croppedAreaPixels;
    };

    const onCropChange = (nextCrop: { x: number; y: number }): void => {
        setCrop((currentCrop) => {
            if (currentCrop.x === nextCrop.x && currentCrop.y === nextCrop.y) {
                return currentCrop;
            }

            return nextCrop;
        });
    };

    const cancel = (): void => {
        resetStates();
        onClose();
    };

    const save = async (): Promise<void> => {
        const cropImage = await getCroppedImg(
            src,
            croppedAreaPixelsRef.current,
            rotation,
            fileType,
        );
        onChange(cropImage);
        cancel();
    };

    return (
        <Modal
            closeIcon={null}
            destroyOnHidden={true}
            footer={[
                <Button key='cancel' onClick={cancel}>
                    Cancelar
                </Button>,
                <Button key='ok' onClick={save} type='primary'>
                    Salvar
                </Button>,
            ]}
            onCancel={cancel}
            open={open}
            style={{ height: 380, position: 'relative' }}
            title={fileName}
            width={520}
            zIndex={1050}
        >
            <div className='relative h-64 w-full'>
                {open && (
                    <Cropper
                        aspect={1}
                        crop={crop}
                        image={src}
                        onCropChange={onCropChange}
                        onCropComplete={onCropComplete}
                        onRotationChange={setRotation}
                        onZoomChange={setZoom}
                        rotation={rotation}
                        showGrid={false}
                        zoom={zoom}
                    />
                )}
            </div>

            {/* Floating slider tooltips can loop while realigning during dragging. */}
            <div className='flex w-full items-center gap-2'>
                <MinusOutlined />
                <Slider
                    aria-label='Zoom'
                    className='w-full'
                    max={3}
                    min={1}
                    onChange={setZoom}
                    step={0.1}
                    tooltip={{ open: false }}
                    value={zoom}
                />
                <PlusOutlined />
                <span className='w-12 shrink-0 text-right tabular-nums'>{zoom.toFixed(1)}×</span>
            </div>

            <div className='flex w-full items-center gap-2'>
                <UndoOutlined />
                <Slider
                    aria-label='Rotação'
                    className='w-full'
                    max={360}
                    min={0}
                    onChange={setRotation}
                    step={1}
                    tooltip={{ open: false }}
                    value={rotation}
                />
                <RedoOutlined />
                <span className='w-12 shrink-0 text-right tabular-nums'>
                    {Math.round(rotation)}°
                </span>
            </div>
        </Modal>
    );
}
